import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { Button } from '../components/ui/Button'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import type { SantaBooking, TimeSlot } from '../lib/types'

interface BookingWithOrder extends SantaBooking {
  order: {
    fullName: string
    email: string
    paymentId: string | null
    receiptId: string | null
    createdAt: string
    totalAmount: number
  }
}

export default function BookingsPage() {
  const [bookings, setBookings] = useState<BookingWithOrder[]>([])
  const [slots, setSlots] = useState<TimeSlot[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showOrderDetails, setShowOrderDetails] = useState(true)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    loadBookings()
  }, [])

  const loadBookings = async () => {
    setIsLoading(true)
    setLoadError(false)
    try {
      const [bookingsData, slotsData] = await Promise.all([
        api.getAdminBookings(),
        api.getAdminSlots(),
      ])
      setBookings(bookingsData as BookingWithOrder[])
      setSlots(slotsData)
    } catch (error) {
      console.error('Failed to load bookings:', error)
      setLoadError(true)
    } finally {
      setIsLoading(false)
    }
  }

  const exportToCSV = () => {
    const headers = ['Time', 'Slot', 'Parent Name', 'Email', 'Order ID', 'Receipt ID', 'SumUp Ref', 'Booked Date', 'Children', 'Dietary Requirements']
    const rows = bookings.map(booking => [
      booking.timeSlot?.time || '',
      booking.timeSlot?.slotNumber || '',
      booking.order.fullName,
      booking.order.email,
      booking.orderId,
      booking.order.receiptId || 'N/A',
      booking.order.paymentId || 'N/A',
      new Date(booking.order.createdAt).toLocaleString(),
      JSON.parse(booking.childrenNames).join('; '),
      booking.dietaryRequirements || 'None',
    ])

    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `santa-bookings-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Group bookings by time
  const bookingsByTime = bookings.reduce((acc, booking) => {
    const time = booking.timeSlot?.time || 'Unknown'
    if (!acc[time]) {
      acc[time] = []
    }
    acc[time].push(booking)
    return acc
  }, {} as Record<string, BookingWithOrder[]>)

  // Calculate overview statistics
  const totalChildren = bookings.reduce((sum, booking) => {
    return sum + JSON.parse(booking.childrenNames).length
  }, 0)

  const childrenWithDietary = bookings.filter(booking => booking.dietaryRequirements && booking.dietaryRequirements.trim() !== '')

  const dietaryDetails = childrenWithDietary.map(booking => ({
    parent: booking.order.fullName,
    children: JSON.parse(booking.childrenNames),
    requirements: booking.dietaryRequirements
  }))

  // Calculate total revenue from completed bookings
  const totalRevenue = bookings.reduce((sum, booking) => {
    return sum + booking.order.totalAmount
  }, 0)

  // Detect overbooking by checking slots per time period
  const slotsByTime = slots.reduce((acc, slot) => {
    if (!acc[slot.time]) {
      acc[slot.time] = []
    }
    acc[slot.time].push(slot)
    return acc
  }, {} as Record<string, TimeSlot[]>)

  const overbookingIssues = Object.entries(bookingsByTime).map(([time, timeBookings]) => {
    const availableSlots = slotsByTime[time]?.length || 0
    const bookedSlots = timeBookings.length
    return {
      time,
      bookedSlots,
      availableSlots,
      isOverbooked: bookedSlots > availableSlots
    }
  }).filter(issue => issue.isOverbooked)

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Loading bookings...</p>
      </div>
    )
  }

  if (loadError) {
    return (
      <div role="alert" className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex flex-wrap items-center gap-3">
        <span>Could not load bookings. Check your connection and try again.</span>
        <button onClick={() => loadBookings()} className="min-h-[44px] px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Santa Visit Schedule</h1>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showOrderDetails}
              onChange={(e) => setShowOrderDetails(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            Show order details
          </label>
          <Button onClick={exportToCSV} variant="outline">
            Export to CSV
          </Button>
        </div>
      </div>

      {/* Overbooking Warnings */}
      {overbookingIssues.length > 0 && (
        <Card variant="bordered">
          <CardContent>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-red-900 mb-2">Overbooking Alert!</h3>
              <p className="text-sm text-red-700 mb-3">The following time slots have more bookings than available slots:</p>
              <ul className="space-y-2">
                {overbookingIssues.map(issue => (
                  <li key={issue.time} className="text-sm text-red-800 font-medium">
                    {issue.time}: {issue.bookedSlots} bookings but only {issue.availableSlots} slots available
                    <span className="text-red-600"> (+{issue.bookedSlots - issue.availableSlots} overbooked)</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overview Statistics */}
      {bookings.length > 0 && (
        <Card variant="bordered">
          <CardContent>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Bookings */}
              <div className="space-y-1">
                <p className="text-sm text-gray-600">Total Bookings</p>
                <p className="text-2xl font-bold text-gray-900">{bookings.length}</p>
              </div>

              {/* Total Children */}
              <div className="space-y-1">
                <p className="text-sm text-gray-600">Total Children</p>
                <p className="text-2xl font-bold text-gray-900">{totalChildren}</p>
              </div>

              {/* Children with Dietary Requirements */}
              <div className="space-y-1">
                <p className="text-sm text-gray-600">Dietary Requirements</p>
                <p className="text-2xl font-bold text-gray-900">{childrenWithDietary.length}</p>
                <p className="text-xs text-gray-500">{childrenWithDietary.length} {childrenWithDietary.length === 1 ? 'family' : 'families'}</p>
              </div>

              {/* Total Revenue */}
              <div className="space-y-1">
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">£{totalRevenue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            </div>

            {/* Dietary Requirements Details */}
            {dietaryDetails.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Dietary Requirements Details</h4>
                <div className="space-y-3">
                  {dietaryDetails.map((detail, idx) => (
                    <div key={idx} className="border border-amber-200 rounded-lg p-3 bg-amber-50">
                      <p className="text-sm font-medium text-gray-900">{detail.parent}</p>
                      <p className="text-xs text-gray-600 mt-1">Children: {detail.children.join(', ')}</p>
                      <p className="text-sm text-amber-900 mt-2">
                        <span className="font-medium">Requirements:</span> {detail.requirements}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {bookings.length === 0 ? (
        <Card>
          <CardContent>
            <p className="text-center text-gray-600 py-8">No bookings yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(bookingsByTime).map(([time, timeBookings]) => {
            const availableSlots = slotsByTime[time]?.length || 0
            const bookedSlots = timeBookings.length
            const isOverbooked = bookedSlots > availableSlots

            return (
              <Card key={time} variant="elevated">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-gray-900">{time}</CardTitle>
                    {isOverbooked ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-300">
                        Overbooked: {bookedSlots}/{availableSlots} slots (+{bookedSlots - availableSlots})
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        {bookedSlots}/{availableSlots} slots
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {timeBookings
                    .sort((a, b) => (a.timeSlot?.slotNumber || 0) - (b.timeSlot?.slotNumber || 0))
                    .map((booking) => {
                      const childrenCount = JSON.parse(booking.childrenNames).length
                      return (
                        <div
                          key={booking.id}
                          className="border border-gray-200 rounded-lg p-4 bg-white"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              Slot {booking.timeSlot?.slotNumber}
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {childrenCount} {childrenCount === 1 ? 'child' : 'children'}
                            </span>
                          </div>
                        <h4 className="font-semibold text-gray-900">
                          {booking.order.fullName}
                        </h4>
                        {showOrderDetails && (
                          <>
                            <p className="text-sm text-gray-600">
                              {booking.order.email}
                            </p>
                            <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                              <p>
                                Order ID: <span className="font-mono">{booking.orderId}</span>
                              </p>
                              {booking.order.receiptId && (
                                <p>
                                  Receipt ID: <span className="font-mono">{booking.order.receiptId}</span>
                                </p>
                              )}
                              {booking.order.paymentId && (
                                <p>
                                  SumUp Ref: <span className="font-mono">{booking.order.paymentId}</span>
                                </p>
                              )}
                              <p>
                                Booked: {new Date(booking.order.createdAt).toLocaleString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </>
                        )}
                        <div className={`space-y-1 ${showOrderDetails ? 'mt-2 pt-2 border-t border-gray-100 text-sm' : 'mt-3 text-lg'}`}>
                          <p className="text-gray-700">
                            <span className="font-medium">Children:</span>{' '}
                            {JSON.parse(booking.childrenNames).join(', ')}
                          </p>
                          {booking.dietaryRequirements && (
                            <p className={`px-2 py-1 bg-amber-50 border border-amber-200 rounded text-amber-900 ${showOrderDetails ? 'text-sm' : 'text-base'}`}>
                              <span className="font-medium">Dietary:</span>{' '}
                              {booking.dietaryRequirements}
                            </p>
                          )}
                        </div>
                      </div>
                      )
                    })}
                </div>
              </CardContent>
            </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
